/**
 * Platform-neutral error reporting port.
 *
 * The shared data engine (Firestore query/document subscriptions, client models)
 * and the widget render trunk must report errors without hard-depending on
 * `@sentry/react`, which pulls DOM/browser-only code and blocks importing the
 * data layer on React Native. This module defines a small `ErrorReporter` port
 * plus a module-level holder; the web app injects a Sentry-backed adapter at
 * bootstrap (see `lib/sentry.ts`), while other platforms can inject their own.
 *
 * The shape mirrors the subset of the Sentry API the codebase actually uses, so
 * the adapter is a thin pass-through.
 */

import { isOfflineFlowError } from './flow-utils.js';

/** Severity levels, matching Sentry's `SeverityLevel`. */
export type ErrorSeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

/** Tag values accepted by a scope/capture call. */
export type ErrorTagValue = string | number | boolean | null | undefined;

/** Neutral scope alias used inside `withScope` callbacks. */
export interface ErrorScope {
  setTag(key: string, value: ErrorTagValue): void;
  setContext(name: string, context: Record<string, unknown> | null): void;
  setLevel(level: ErrorSeverityLevel): void;
}

/**
 * Pseudonymous identity attached to reported errors — the Firebase UID and nothing else.
 *
 * Some fatal classes arrive with no stack trace at all (iOS watchdog terminations, OOM kills), so the
 * account is often the only handle triage has on who hit it and what they were doing. Deliberately
 * just the id: no email, name, or phone, so a crash payload never carries the client PII this product
 * handles.
 */
export interface ErrorUser {
  id: string;
}

/** Structured context attached to a capture call. */
export interface CaptureContext {
  tags?: Record<string, ErrorTagValue>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  level?: ErrorSeverityLevel;
}

/** The error reporting port. */
export interface ErrorReporter {
  captureException(error: unknown, context?: CaptureContext): void;
  captureMessage(message: string, context?: CaptureContext): void;
  withScope(callback: (scope: ErrorScope) => void): void;
  /** Associates the signed-in account with every subsequent report; `undefined` clears it. */
  setUser(user: ErrorUser | undefined): void;
}

/** No-op scope used by the default reporter. */
const noopScope: ErrorScope = {
  setTag() {},
  setContext() {},
  setLevel() {},
};

/**
 * Default reporter: does nothing. Used until a platform injects a real reporter,
 * and on platforms (or in tests) where error reporting is not configured.
 */
export const noopErrorReporter: ErrorReporter = {
  captureException() {},
  captureMessage() {},
  withScope(callback) {
    callback(noopScope);
  },
  setUser() {},
};

let currentReporter: ErrorReporter = noopErrorReporter;

/** Injects the active error reporter (call once at app bootstrap). */
export function setErrorReporter(reporter: ErrorReporter): void {
  currentReporter = reporter;
}

/** Returns the active error reporter. */
export function getErrorReporter(): ErrorReporter {
  return currentReporter;
}

/**
 * Whether a captured error should reach the error tracker at all. Each platform's Sentry
 * `beforeSend` applies it, so it covers every capture path, unhandled rejections included.
 *
 * A flow request that failed because the device is genuinely offline is an expected condition the
 * UI already explains, not a defect, and reporting it buried the real signal: requests failing while
 * the device reads as *online* (ELARA-1146). Those are still reported.
 */
export function isReportableError(error: unknown): boolean {
  return !isOfflineFlowError(error);
}
